import * as React from 'react';
import { FacilityType, rng, stringUtil } from '../../amorg-common';
import { Location } from '../../amorg-common/DTOs/Location';
import { createLocation, deleteLocation, getFacilityTypes, getLocations } from '../../api/locationClientApi';
import { Button } from '../../designSystem/button/Button';
import { Card } from '../../designSystem/Card';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../designSystem/Message';
import { Modal } from '../../designSystem/Modal';
import { PageHeader } from '../../designSystem/PageHeader';
import { PageSection } from '../../designSystem/PageSection';
import { P } from '../../designSystem/typography/Body';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';
import FacilityList from './_components/FacilityList';
import { LocationEditor } from './_components/LocationEditor';
import PersonList from './_components/PersonList';
import { MainLeftTwoColumnLayout } from '../_layouts/TwoColumnLayouts';

type State = {
	loading:boolean,
	locations:Location[],
    allFacilityTypes:FacilityType[]
}

export default function LocationListingPage(): JSX.Element {
    return (
        <MemberPageWrapper selectedMenuItemKey='locations' breadCrumbs={[{ text: 'Lokationer', url: '' }]}>
            <LocationListingPageContent />
        </MemberPageWrapper>
    );
}

function LocationListingPageContent(): JSX.Element {
    
    const [inEdit, setInEdit] = React.useState<Location | null>(null);
    const [state, setState] = React.useState<State>({
        loading:true,
        locations:[],
        allFacilityTypes:[]
    });


    React.useEffect(() => {
        getLocationsList();
    }, [])

    
    async function onLocationClose(){
        setInEdit(null);
        getLocationsList()
    }

    async function getLocationsList() {
        const locations = await getLocations();
        const allFacilityTypes = await getFacilityTypes();
        setState({locations, allFacilityTypes, loading:false});
    }

    async function handleCreateLocation(){
        setState({
            ...state,
            loading:true
        });
        const allLocations = await getLocations();
        
        const id = rng.getRandomId('loc_');

        const nameBase = 'Ny lokation';
        const nextName = stringUtil.generateNextSequenceValue(nameBase, allLocations.map(location => location.name));

        const createResult = await createLocation(id, nextName, ``, ``, ``, ``);
        if (createResult.isSuccess) {
            const locations = await getLocations();
            setState({...state, locations, loading:false});
            setInEdit(locations.find(x => x.id === id) || null);
        }
    }


    function handleLocationSelected(locationId:string){
        const location = state.locations.find( x => x.id === locationId)
        setInEdit(location || null);
    }

    async function handleLocationDeleted() {
        setState({...state, loading:true});
        const locations = await getLocations();
        setState({...state, locations, loading:false});
    }

    async function handleLocationUpdated(){
        setState({ ...state, loading:true, })
        const locations = await getLocations();
        setState({...state, locations, loading:false});
        if(inEdit){
            setInEdit(locations.find(x => x.id === inEdit.id) || null);
        }
    }

    const protectedUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(protectedUser, ['ViewLocations']);

    return (
        <>
            <PageHeader 
                narrow
                headerIcon='navigation_home'
                headerText="Lokationer" 
                headerStyle='secondary-gray'
                buttons={
                    <Button variant="filled" icon="actions_plus" size={"42px"} emphasize onClick={handleCreateLocation} text="Opret ny lokation" />
                } 
            />

        <SingleColumnLayout narrow>
            <>
                <LoadingPageOverlay loading={state.loading} />

                {!hasAccess
                    ? <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>
                    :
                    <>

                    <MainLeftTwoColumnLayout
                        main={
                            <>
                            {(!state.locations || state.locations?.length === 0) && 
                                <>
                                    <MessageCard variant='info'>
                                        Ingen lokationer oprettet
                                    </MessageCard>
                                </>
                                }

                            <Flex flexDirection="column" gap={24}>
                                {state.locations && state.locations.map(l => {
                                    return (
                                        <LocationView 
                                            location={l}
                                            allFacilityTypes={state.allFacilityTypes}
                                            key={l.id} 
                                            onLocationDeleted={handleLocationDeleted} 
                                            onLocationSelected={() => handleLocationSelected(l.id)}
                                            onLocationUpdated={handleLocationUpdated}
                                        />
                                    );
                                })}
                                
                            </Flex>
                            </>
                        }
                        right={
                            
                            <PersonList />
                            
                        } 
                    />
        
                    <Modal 
                        title={"Opret lokation"}
                        content={
                            <LocationEditor 
                                location={inEdit as Location} 
                                onLocationUpdated={handleLocationUpdated}
                                onClose={ onLocationClose }
                            />
                        }
                        showModal={inEdit !== null}
                        onCancel={onLocationClose} />
                    </>
                }
            </>
        </SingleColumnLayout>
        </>
    )
}


type LocationViewProps = {
    location:Location,
    allFacilityTypes:FacilityType[],
    onLocationDeleted: ()=>void,
    onLocationSelected: ()=>void,
    onLocationUpdated: ()=> Promise<void>
}

export function LocationView(props:LocationViewProps):JSX.Element
{
    const [errorMessage, setErrorMessage] = React.useState('');

    React.useEffect(() => {
        setErrorMessage('');
    }, [props.location]);

    async function handleClickDeleteLocation() {
        setErrorMessage('');
        if(props.location.facilities.length > 0){
            setErrorMessage('Kan ikke slette lokationen, da den er tilknyttet faciliteter');
        }
        else{
            const result = await deleteLocation(props.location.id);
            if(result.isSuccess){
                props.onLocationDeleted()
            }
        }
    }
    
    return (
        <>
        {errorMessage &&
            <MessageCard variant='error'>
                Lokationen kan ikke slettes, da den er tilknyttet faciliteter
            </MessageCard>
        }

        <PageSection headerStyleVariant='minimal' contentStyleVariant='card'

            header={{
                title: props.location.name,
                defaultCollapsed: false,
                padding: '0px',
                popupMenuButton:{
                    buttonVariant: 'text',
                    entries:[
                        { type:'menuItem', text:'Rediger lokation', icon: 'interface_actions_edit', onClick:props.onLocationSelected },
                        { type:'menuItem', text:'Slet lokation', icon: 'interface_actions_delete', onClick:handleClickDeleteLocation }
                    ]
                }
            }}
        >
                <Card variant="lightGray" style={{marginBottom: 16}}>
                    <div>
                        {props.location.address}
                    </div>
                    <Flex gap={8}>
                        <span>{props.location.postalCode}</span>
                        <span>{props.location.city}</span>
                    </Flex>
                </Card>

                <FacilityList locationId={props.location.id} facilities={props.location.facilities} allFacilityTypes={props.allFacilityTypes} onUpdated={props.onLocationUpdated}/>

        </PageSection>
    </>
    )
}